import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Bot, Pencil, Trash2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useApi, useQ, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Empty, AgentAvatar } from "@/components/common";
import AgentForm from "@/components/AgentForm";
import AgentTestChat from "@/components/AgentTestChat";

const AgentCard = ({ agent, onEdit, onTest, onDelete }) => {
  const { t } = useT();
  return (
    <Panel className="flex flex-col gap-4 fade-up" data-testid={`agent-card-${agent.id}`}>
      <div className="flex items-center gap-4">
        <AgentAvatar agent={agent} size="h-14 w-14" />
        <div className="min-w-0">
          <p className="font-display text-xl font-bold truncate">{agent.name}</p>
          <p className="text-xs text-slate-400 uppercase tracking-wider">{agent.language} · {t(`len_${agent.response_length}`)} · {Math.round(agent.creativity * 100)}%</p>
        </div>
      </div>
      <p className="text-sm text-slate-400 line-clamp-2 min-h-[2.5rem]">{agent.personality || t("no_personality")}</p>
      <div className="flex gap-2 mt-auto">
        <Button size="sm" className="flex-1 bg-indigo-600 hover:bg-indigo-500" onClick={onTest} data-testid={`agent-test-btn-${agent.id}`}><MessageCircle className="h-4 w-4 mr-1" />{t("test")}</Button>
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`agent-edit-btn-${agent.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" onClick={onDelete} className="hover:text-rose-400" data-testid={`agent-delete-btn-${agent.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </Panel>
  );
};

export default function Agents() {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const { data: agents = [], isLoading } = useQ("/agents");
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/agents/${editing.id}`, form);
      else await api.post("/agents", form);
      toast.success(t("agent_saved"));
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["/agents"] });
      qc.invalidateQueries({ queryKey: ["/overview"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`${t("confirm_delete")} ${a.name}?`)) return;
    try { await api.delete(`/agents/${a.id}`); qc.invalidateQueries({ queryKey: ["/agents"] }); toast.success(t("deleted")); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const newBtn = <Button onClick={() => setEditing({})} className="rounded-full bg-indigo-600 hover:bg-indigo-500 shadow-[0_0_24px_rgba(79,70,229,0.35)]" data-testid="new-agent-btn"><Plus className="h-4 w-4 mr-1" />{t("new_agent")}</Button>;

  return (
    <div>
      <PageHeader eyebrow={t("nav_agents")} title={t("agents_title")} subtitle={t("agents_sub")} action={newBtn} />
      {!isLoading && agents.length === 0 ? (
        <Empty icon={Bot} title={t("no_agents")} text={t("no_agents_hint")} action={newBtn} testId="agents-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {agents.map((a) => <AgentCard key={a.id} agent={a} onEdit={() => setEditing(a)} onTest={() => setTesting(a)} onDelete={() => remove(a)} />)}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111118] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? t("edit_agent") : t("new_agent")}</DialogTitle></DialogHeader>
          {editing && <AgentForm initial={editing} onSubmit={save} saving={saving} />}
        </DialogContent>
      </Dialog>
      <Sheet open={!!testing} onOpenChange={(o) => !o && setTesting(null)}>
        <SheetContent className="bg-[#0B0B10] border-white/10 w-full sm:max-w-lg flex flex-col">
          <SheetHeader><SheetTitle className="font-display">{t("test")} · {testing?.name}</SheetTitle></SheetHeader>
          {testing && <AgentTestChat agent={testing} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
